import { isGooglePlatform } from '@/helpers';
import { relativeTimingLabel, tooltipDisplayLifecycleEventName } from '@/helpers/displayHelpers';
import { RootState } from '@/reducers/reducers';
import {
    selectDestinationInfoByDestinationStepConfigId,
    selectIsWorkflowsMigratedAndEnabled,
    selectStepConfigDataFromStepGraphs,
    selectWorkflowEnabledSteps,
} from '@/selectors/selectors';
import { AppPlatform, LifecycleEventType, RelativeTiming, ReleaseStepType } from '@/types';
import { Nullable } from '@/typescriptHelpers';
import React from 'react';
import { useSelector } from 'react-redux';

enum DestinationSelector {
    ANY = 'Any',
    ALL = 'All',
}

export const useRelativeTimeTriggerOptions = (
    appId: string,
    appPlatform: AppPlatform,
    stepConfigId: Nullable<string>,
) => {
    const isWorkflowsEnabled = useSelector((state: RootState) => selectIsWorkflowsMigratedAndEnabled(state, appId));
    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, appId));
    const stepGraphStepsMap =
        appId ? useSelector((state: RootState) => selectStepConfigDataFromStepGraphs(state, appId)) : null;
    const destinationStepConfigIds =
        stepGraphStepsMap ? (stepGraphStepsMap[stepConfigId]?.destinationStepConfigIds ?? []) : [];
    const destinationsByStepConfigId = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, appId),
    );

    const destinations = React.useMemo(() => {
        return destinationStepConfigIds.map((d) => destinationsByStepConfigId[d]).filter((d) => !!d);
    }, [destinationStepConfigIds, destinationsByStepConfigId]);

    const isWorkflowsEnabledAndMultiDestination = React.useMemo(() => {
        return isWorkflowsEnabled && destinations.length > 1;
    }, [isWorkflowsEnabled, destinations]);

    const shouldHideReleaseEvent = React.useMemo(() => {
        const platforms = destinations.length > 0 ? destinations.map((d) => d.platform) : [appPlatform];
        return platforms.every(isGooglePlatform);
    }, [destinations, appPlatform]);

    const relativeTimingOptions = Object.values(RelativeTiming).map((value: RelativeTiming) => ({
        value,
        label: relativeTimingLabel(value),
    }));

    const createOptionForEvent = (event: LifecycleEventType, destinationSelector?: DestinationSelector) => {
        if (!destinationSelector) {
            return { value: event, label: tooltipDisplayLifecycleEventName(event) };
        }

        return {
            value: `${event}:${destinationSelector}`,
            label: `${tooltipDisplayLifecycleEventName(event)} (${destinationSelector})`,
        };
    };

    const lifecycleEventOptions = React.useMemo(() => {
        const options = [createOptionForEvent(LifecycleEventType.LifecycleEventTypeKickoff)];

        if (isWorkflowsEnabledAndMultiDestination && workflowEnabledSteps[ReleaseStepType.Submission]) {
            options.push(createOptionForEvent(LifecycleEventType.LifecycleEventTypeSubmit, DestinationSelector.ANY));
            options.push(createOptionForEvent(LifecycleEventType.LifecycleEventTypeSubmit, DestinationSelector.ALL));
        } else {
            options.push(createOptionForEvent(LifecycleEventType.LifecycleEventTypeSubmit));
        }

        if (shouldHideReleaseEvent) {
            // Returning early without release event
            return options;
        }

        if (isWorkflowsEnabledAndMultiDestination && workflowEnabledSteps[ReleaseStepType.Submission]) {
            options.push(createOptionForEvent(LifecycleEventType.LifecycleEventTypeRelease, DestinationSelector.ANY));
            options.push(createOptionForEvent(LifecycleEventType.LifecycleEventTypeRelease, DestinationSelector.ALL));
        } else {
            options.push(createOptionForEvent(LifecycleEventType.LifecycleEventTypeRelease));
        }

        return options;
    }, [isWorkflowsEnabledAndMultiDestination, workflowEnabledSteps]);

    const parseSelectedLifecycleEventOption = React.useCallback((value: string) => {
        const [lifecycleEventType, waitForAllDestinations] = value.split(':');
        return {
            lifecycleEventType: lifecycleEventType as LifecycleEventType,
            waitForAllDestinations: !waitForAllDestinations || waitForAllDestinations === DestinationSelector.ALL,
        };
    }, []);

    const convertToSelectedLifecycleEventValue = React.useCallback(
        (eventType: LifecycleEventType, waitForAllDestinations: boolean) => {
            const selector = waitForAllDestinations ? DestinationSelector.ALL : DestinationSelector.ANY;
            const { value } = createOptionForEvent(eventType, selector);
            return lifecycleEventOptions.find((option) => option.value === value) ? value : eventType;
        },
        [lifecycleEventOptions],
    );

    return {
        relativeTimingOptions,
        lifecycleEventOptions,
        parseSelectedLifecycleEventOption,
        convertToSelectedLifecycleEventValue,
    };
};
